// frontend/src/pages/games/MemoryGame.tsx

import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMemoryGame } from '../../hooks/games/useMemoryGame';
import { useGameSessionTracking } from '../../hooks/games/useGameSessionTracking';
import { MemoryBoard } from '../../components/games/memory/MemoryBoard';
import { DifficultySelector } from '../../components/games/memory/DifficultySelector';
import { GameCompleteModal } from '../../components/games/GameCompleteModal';
import '../../styles/games.css';

const GAME_ID = 'memory';
const MEMORY_COMPLETE_XP = 15;

export function MemoryGamePage() {
  const navigate = useNavigate();
  const {
    difficulty,
    gridSize,
    cards,
    moves,
    matchedPairs,
    totalPairs,
    isComplete,
    flipCard,
    startNewGame,
  } = useMemoryGame('easy');

  const { recordCompletion, resetRoundTimer } = useGameSessionTracking(GAME_ID);
  const recordedRef = useRef(false);
  const roundStartedAtRef = useRef(Date.now());
  const [xpEarned, setXpEarned] = useState<number | null>(null);

  useEffect(() => {
    if (isComplete && !recordedRef.current) {
      recordedRef.current = true;
      const isPerfect = moves === totalPairs; // hiç yanlış çevirme yapılmadı
      const durationMs = Date.now() - roundStartedAtRef.current;
      const score = Math.max(0, 1000 - moves * 10);
      const { xpAwarded } = recordCompletion({
        score,
        xpAmount: MEMORY_COMPLETE_XP,
        session: { isPerfect, durationMs },
      });
      setXpEarned(xpAwarded);
    }
    if (!isComplete) {
      recordedRef.current = false;
    }
  }, [isComplete, moves, totalPairs, recordCompletion]);

  const handleRestart = (nextDifficulty?: typeof difficulty) => {
    resetRoundTimer();
    roundStartedAtRef.current = Date.now();
    setXpEarned(null);
    startNewGame(nextDifficulty);
  };

  return (
    <div className="game-center-page memory-game-page">
      <header className="game-center-header">
        <div>
          <h1>🧠 Hafıza Oyunu</h1>
          <p>Kartları çevir, eşlerini bul!</p>
        </div>
        <Link to="/games" className="game-card-btn secondary sm">
          ← Oyun Merkezi
        </Link>
      </header>

      <div className="memory-toolbar">
        <DifficultySelector current={difficulty} onSelect={(d) => handleRestart(d)} />

        <div className="memory-stats">
          <span>🔁 Hamle: {moves}</span>
          <span>✅ {matchedPairs}/{totalPairs} Eşleşme</span>
        </div>

        <button type="button" className="game-card-btn secondary sm" onClick={() => handleRestart()}>
          🔄 Yeniden Başlat
        </button>
      </div>

      <MemoryBoard cards={cards} gridSize={gridSize} onFlip={flipCard} />

      {isComplete && (
        <GameCompleteModal
          message={`${totalPairs} eşleşmeyi ${moves} hamlede tamamladın!`}
          xpEarned={xpEarned ?? undefined}
          onPlayAgain={() => handleRestart()}
          onReturnToCenter={() => navigate('/games')}
        />
      )}
    </div>
  );
}

